/**
 * @file   src\Pages\AddCarWash.tsx
 * @brief  AddCarWash page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useEffect, useOutletContext, Container, Tab, Tabs, Button, Breadcrumb, Row, Col, useNavigate, useRef, useParams, useLocation, React } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType, adminbankinfoFormData } from '../interfaces/generalInterface';
import { formatPhoneNumber } from '../utils/helper';
import * as Yup from 'yup'
import { CITY_MAX_LENGTH, EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PHONE_MAX_LENGTH, WASH_ADDRESS_MAX_LENGTH, WASH_NAME_MAX_LENGTH } from '../utils/constants';
import { editCarwashSchema } from '../validations/addCarwashSchema';
import { CarwashItem, CompanyItem, CountryItem, Option } from '../interfaces/CarWashInterface';
import axios from '../config/axiosConfig';
import { CarWashApi, CompanyApi } from '../utils/apiUrls';
import { fetchStatesByCountry, fetchSupportedCountries, getCountryCodeFromRecord, getCountryFieldLabels, getPostalCodeMaxLength, getSelectedOptions, isAllowedPostalCodeInput, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import { MessageToaster } from '../utils/toastUtil';
import FullLoader from '../components/FullLoader';
import { PaymentType } from '../utils/enums';
import { addBankInfoSchema } from '../validations/addBankInfoSchema';
import { hasInvalidStripeSecretKey, STRIPE_SECRET_KEY_LIVE_ERROR, stripePublicKeyCwInputLabel, stripeSecretKeyCwInputLabel } from '../components/stripePaymentInputLabels';

const EditCarwash = () => {

    const navigate = useNavigate()

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    // State to handle options for country and state
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [options, setOptions] = useState<Option[]>([]);
    const countryOptions = mapCountryOptions(supportedCountries);

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);

    const { id } = useParams<{ id: string }>();

    // Initialize states for formdata
    const [washData, setWashData] = useState<Partial<CarwashItem>>({})
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, washData.country);
    const postalMaxLength = getPostalCodeMaxLength(washData.country);

    // Initialise states for errors
    const [errors, setErrors] = useState({
        washname: '',
        address: '',
        address_2: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        name: '',
        emailid: '',
        phonenumber: ''
    });

    // Instantiate MessageToaster
    const toaster = new MessageToaster();

    const [loading, setLoading] = useState(false);

    const location = useLocation()

    const page = location.state?.page || 1;

    const search = location.state?.search || '';

    const sortOrder = location.state?.sortOrder;


    useEffect(() => {
        // To display heading
        handleUpdate()

        // Get supported countries and company details
        getCountries()
        getCompany()
    }, [])

    const getCompany = async () => {
        try {
            const { data } = await axios.get(CompanyApi.GET_COMPANY_DETAILS_BY_ID + id);
            if (data.error === 0) {
                const company: CompanyItem = data.data
                const countryCode = getCountryCodeFromRecord(company)
                const washData: CarwashItem = {
                    washname: company.companyName,
                    address: company.address1,
                    address_2: company.address2,
                    city: company.city,
                    country: countryCode,
                    state: company.state,
                    zipcode: company.zipCode,
                    name: company.contactName,
                    emailid: company.email,
                    phonenumber: company.phoneNumber
                }
                const stateOptions = await fetchStatesByCountry(countryCode);
                setOptions(stateOptions)
                setWashData(washData)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };


    // Get supported countries
    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries)
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    }

    //Get States
    const getStates = async (countryCode: string) => {
        try {
            const stateOptions = await fetchStatesByCountry(countryCode);
            setOptions(stateOptions)
        } catch (error: any) {
            setOptions([])
            toaster.toastError(error.message);
        }
    }

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Edit Car Wash", url: sortOrder ? `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}&sortOrder=${sortOrder}` : `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}`, showBackButton: true, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'country') {
            setErrors({
                ...errors,
                country: '',
                state: '',
                zipcode: '',
            });
            setWashData({
                ...washData,
                country: value,
                state: '',
                zipcode: '',
            });
            getStates(value);
            return;
        }

        setWashData({
            ...washData,
            [name]: value,
        });

    };

    // Triggers when input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        // To allow only digits in phone number field
        if ((event.target.name === "phonenumber")) {
            // Extract only digits from the input value
            const new_value = event.target.value;
            if (!/^\d*$/.test(new_value)) {
                event.target.value = new_value.replace(/\D/g, '');
                setWashData({
                    ...washData,
                    [name]: value,
                });
            }
        } else if ((event.target.name === "zipcode")) {

            const new_value = normalizePostalCodeInput(event.target.value, washData.country);
            if (isAllowedPostalCodeInput(new_value, washData.country)) {
                setWashData({
                    ...washData,
                    [name]: new_value,
                });
            }
        } else {
            setWashData({
                ...washData,
                [name]: value,
            });

        }


        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }


        // Change user enters Phone number in usa format
        if (name === 'phonenumber') {
            const formattedValue = formatPhoneNumber(value);
            setWashData({
                ...washData,
                [name]: formattedValue,
            });
        }

    }

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setLoading(true)
            if (washData.phonenumber) {
                washData.phonenumber = formatPhoneNumber(washData.phonenumber)
            }
            // Validate form data
            await editCarwashSchema.validate(washData, { abortEarly: false });
            const carwashId: number = id ? parseInt(id) : 0;
            const newWashData = {
                carWashId: carwashId,
                carWashName: washData.washname && washData.washname.trim(),
                address1: washData.address && washData.address.trim(),
                address2: washData.address_2 && washData.address_2.trim(),
                contactName: washData.name && washData.name.trim(),
                phoneNumber: washData.phonenumber && washData.phonenumber.trim(),
                city: washData.city && washData.city.trim(),
                countryCode: washData.country && washData.country.trim(),
                stateCode: washData.state && washData.state.trim(),
                zipCode: washData.zipcode && washData.zipcode.trim()
            }

            try {
                const { data } = await axios.post(CarWashApi.CARWASH_EDIT, newWashData);

                if (data?.error === 0) {
                    toaster.toastSuccess(data?.message);

                    navigate(sortOrder ? `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}&sortOrder=${sortOrder}` : `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}`)
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }

        } catch (error) {
            setLoading(false);
            const newErrors: any = {};

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {

                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof washData] = err.message;
                });
                setErrors(newErrors);
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }

    }

    // start bank update functions
    const param = useParams();
    const carwashid = param.id ? parseInt(param.id) : 0;

    // State to company bank details
    const [companybank, setCompanybank] = useState({
        PaymentMethod: 1,
        stripeSecretKey: '',
        stripePublicKey: '',
    }
    );

    // To get company bank details
    const getCompanybank = async () => {
        try {
            let param = '';
            param += carwashid;
            const { data } = await axios.get(CompanyApi.GET_COMPANY_BANK_DETAILS + param)
            if (data?.error === 0) {
                const paymentDetails = data.data.paymentDetails;
                if (paymentDetails && paymentDetails !== "") {
                    setCompanybank({
                        PaymentMethod: 1,
                        stripeSecretKey: paymentDetails?.stripeSecretKey,
                        stripePublicKey: paymentDetails?.stripePublicKey,
                    });
                }
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }
    useEffect(() => {
        getCompanybank(); // company bank
    }, []);

    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // Initialize states for admin formData 
    const [adminformData, setAdminFormData] = useState<adminbankinfoFormData>({
        carwashid: carwashid,
        PaymentMethod: companybank.PaymentMethod,
        paymentKeys: {
            stripeSecretKey: '',
            stripePublicKey: '',
        },
    });


    // Initialise states for errors
    const [bankerrors, setBankErrors] = useState({
        paymentType: '',
        stripeSecretKey: '',
        stripePublicKey: '',
    });

    useEffect(() => {
        const savedData = localStorage.getItem('bankInformation');
        if (savedData) {
            setCompanybank(JSON.parse(savedData));
        }
    }, [setCompanybank]);

    // Triggers whenever options is selected
    const handleSelectChangebank = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(bankerrors);
        if (keys.includes(name)) {
            setBankErrors({
                ...bankerrors,
                [name]: ""
            })
        }

        setCompanybank({
            ...companybank,
            [name]: value,
        });

    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(bankerrors);
        if (keys.includes(name)) {
            setBankErrors({
                ...bankerrors,
                [name]: ""
            })
        }

        const newFormData = {
            ...companybank,
            [name]: value.trim(),
        };

        setCompanybank(newFormData);
    };

    useEffect(() => {
        if (companybank.PaymentMethod === 1) {
            setAdminFormData({
                carwashid: carwashid,
                PaymentMethod: companybank.PaymentMethod,
                paymentKeys: {
                    stripeSecretKey: companybank.stripeSecretKey,
                    stripePublicKey: companybank.stripePublicKey
                }
            });
        }
    }, [companybank, carwashid]);

    const saveBankInfo = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (companybank.PaymentMethod === 1 && hasInvalidStripeSecretKey(companybank.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return;
        }
        try {
            // Validate form data
            await addBankInfoSchema.validate(companybank, { abortEarly: false });
            try {

                setLoading(true); // Show loader
                const paymentData = {
                    "PaymentMethod": adminformData.PaymentMethod,
                    "paymentKeys": adminformData.paymentKeys
                }
                const { data } = await axios.post(CompanyApi.VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData)
                if (data?.error !== 0) {
                    toaster.toastError(data?.message);
                    return false;
                }
                const responseLocationBankUpdate = await axios.post(CompanyApi.UPDATE_PAYMENT_METHOD, adminformData);
                if (responseLocationBankUpdate?.data?.error === 0) {
                    //Clear bankerrors
                    setBankErrors({
                        paymentType: '',
                        stripeSecretKey: '',
                        stripePublicKey: ''
                    });

                    // Save form data to localStorage
                    localStorage.setItem('bankInformation', JSON.stringify(companybank));
                    toaster.toastSuccess("Bank details added successfully.");
                    navigate(sortOrder ? `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}&sortOrder=${sortOrder}` : `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}`)

                }
                else {
                    toaster.toastError(responseLocationBankUpdate?.data?.message);
                }

            } catch (error) {
                toaster.toastError('Failed to add Bank Details.');
                console.log(error)
            } finally {
                setLoading(false); // Hide loader
            }
        } catch (error) {
            // If validation fails on submit, then bankerrors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof companybank] = err.message;
                });
                setBankErrors(newErrors);
            }
        }
    }

    return (
        <div className='manage-subscription-container'>
            <Container>
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Wash</Breadcrumb.Item>
                    <Breadcrumb.Item active>Edit Car Wash Details</Breadcrumb.Item>
                </Breadcrumb>
                <div className='cw-form-outer-wrap'>
                    <div className="cw-main-tab">
                        <Tabs
                            defaultActiveKey="carwashinfo"
                            id="profile-tab"
                            className="cw-tab-title justify-content-end"
                        >
                            <Tab eventKey="carwashinfo" title="Car Wash Details">
                                <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                                    <div className="cw-form-container">
                                        <div className="form-inner mx-auto">
                                            <h5>Car Wash Details</h5>
                                            <CWInput
                                                id="washname"
                                                name="washname"
                                                value={washData.washname}
                                                type="text"
                                                label="Car Wash Name"
                                                errorMessage={errors.washname}
                                                maxLength={WASH_NAME_MAX_LENGTH}
                                                isMandatory
                                                onChange={handleInputChange}
                                            />
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="address"
                                                        name="address"
                                                        type="text"
                                                        maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                        value={washData.address}
                                                        label="Address 1"
                                                        errorMessage={errors.address}
                                                        isMandatory
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="address"
                                                        name="address_2"
                                                        value={washData.address_2}
                                                        maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                        type="text"
                                                        label="Address 2"
                                                        labelSuffix="(Optional)"
                                                        errorMessage={errors.address_2}
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="city"
                                                        name="city"
                                                        type="text"
                                                        label="City"
                                                        value={washData.city}
                                                        maxLength={CITY_MAX_LENGTH}
                                                        isMandatory
                                                        errorMessage={errors.city}
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWSelect
                                                        id="country"
                                                        name="country"
                                                        options={countryOptions}
                                                        values={getSelectedOptions(countryOptions, washData.country)}
                                                        label="Country"
                                                        dropdownGap={0}
                                                        dropdownPosition="auto"
                                                        placeholder="Select"
                                                        multi={false}
                                                        isMandatory
                                                        errorMessage={errors.country}
                                                        searchable={false}
                                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'country')}
                                                        className="no-input"
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWSelect
                                                        id="state"
                                                        name="state"
                                                        options={options}
                                                        values={getSelectedOptions(options, washData.state)}
                                                        label={stateLabel}
                                                        dropdownGap={0}
                                                        dropdownPosition="auto"
                                                        placeholder="Select"
                                                        multi={false}
                                                        isMandatory
                                                        errorMessage={errors.state}
                                                        searchable={false}
                                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'state')}
                                                        className="no-input"
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="zipcode"
                                                        name="zipcode"
                                                        type="text"
                                                        value={washData.zipcode}
                                                        label={postalLabel}
                                                        isMandatory
                                                        maxLength={postalMaxLength}
                                                        errorMessage={errors.zipcode}
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                            </Row>
                                            <h5>Contact Information</h5>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="name"
                                                        name="name"
                                                        type="text"
                                                        label="Name"
                                                        value={washData.name}
                                                        maxLength={NAME_MAX_LENGTH}
                                                        isMandatory
                                                        onChange={handleInputChange}
                                                        errorMessage={errors.name}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <div className="custom-note position-relative">
                                                        <CWInput
                                                            id="emailid"
                                                            name="emailid"
                                                            value={washData.emailid}
                                                            type="text"
                                                            label="Email Address"
                                                            isMandatory
                                                            onChange={handleInputChange}
                                                            maxLength={EMAIL_MAX_LENGTH}
                                                            errorMessage={errors.emailid}
                                                            disabled
                                                        />
                                                        <span className="note p-r">This will be used to sign in to the website</span>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="phonenumber"
                                                        name="phonenumber"
                                                        type="text"
                                                        value={washData.phonenumber && formatPhoneNumber(washData.phonenumber)}
                                                        label="Phone Number"
                                                        isMandatory
                                                        maxLength={PHONE_MAX_LENGTH}
                                                        errorMessage={errors.phonenumber}
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                    <div className="btn-sec d-flex justify-content-end">
                                        <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(sortOrder ? `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}&sortOrder=${sortOrder}` : `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}`)}>
                                            Cancel
                                        </Button>
                                        <Button title="Save" variant="primary" className="primary-btn" type="submit" >Save</Button>
                                    </div>
                                </form>
                            </Tab>
                            <Tab eventKey="bankinginfo" title="Payment Method Info">
                                <form onSubmit={(e) => saveBankInfo(e)}>
                                    <div className='cw-form-container'>
                                        <div className="form-inner mx-auto">
                                            <h5>Payment Method Info</h5>
                                            <CWSelect
                                                id="PaymentMethod"
                                                name="PaymentMethod"
                                                options={paymentOptions}
                                                label="Select Merchant Payment Type"
                                                errorMessage={bankerrors.paymentType && bankerrors.paymentType}
                                                dropdownGap={0}
                                                dropdownPosition="auto"
                                                values={paymentOptions.filter(option => option.value === companybank.PaymentMethod)}
                                                onChange={(selectedOptions) => handleSelectChangebank(selectedOptions[0], 'PaymentMethod')}
                                                placeholder="Select Merchant Payment Type"
                                                multi={false}
                                                searchable={false}
                                                isMandatory
                                            />
                                            {companybank.PaymentMethod === 1 && (
                                                <>
                                                    <CWInput
                                                        id="stripeSecretKey"
                                                        name="stripeSecretKey"
                                                        type="text"
                                                        label={stripeSecretKeyCwInputLabel}
                                                        value={companybank.stripeSecretKey}
                                                        onChange={handleChange}
                                                        errorMessage={bankerrors.stripeSecretKey}
                                                        ref={(el) => (refs.current.stripeSecretKey = el)}
                                                        autoComplete="off"
                                                        isMandatory
                                                        autoFocus
                                                    />
                                                    <CWInput
                                                        id="stripePublicKey"
                                                        name="stripePublicKey"
                                                        type="text"
                                                        label={stripePublicKeyCwInputLabel}
                                                        value={companybank.stripePublicKey}
                                                        onChange={handleChange}
                                                        errorMessage={bankerrors.stripePublicKey}
                                                        isMandatory
                                                        ref={(el) => (refs.current.stripePublicKey = el)}
                                                    />
                                                </>
                                            )}
                                            {companybank.PaymentMethod === 2 && (
                                                <>
                                                    {/* use when world pay is ready */}
                                                    <CWInput
                                                        id="SecretKey"
                                                        name="SecretKey"
                                                        type="text"
                                                        label="SecretKey"
                                                        onChange={handleChange}
                                                        // value={companybank.SecretKey}
                                                        // errorMessage={bankerrors.SecretKey}
                                                        // ref={(el) => (refs.current.SecretKey = el)}
                                                        autoComplete="off"
                                                        isMandatory
                                                        autoFocus
                                                    />
                                                    <CWInput
                                                        id="PublicKey"
                                                        name="PublicKey"
                                                        type="text"
                                                        label="Account Name"
                                                        onChange={handleChange}
                                                        // value={companybank.PublicKey}
                                                        // errorMessage={bankerrors.PublicKey}
                                                        // ref={(el) => (refs.current.PublicKey = el)}
                                                        isMandatory
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="btn-sec d-flex justify-content-end">
                                        <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(sortOrder ? `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}&sortOrder=${sortOrder}` : `/superadmin/${pageURLs.manage_carwash}?page=${page}&search=${search}`)}>
                                            Cancel
                                        </Button>
                                        <Button title="Save" variant="primary" className="primary-btn" type="submit" >Save</Button>
                                    </div>
                                </form>
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            </Container>
            {loading && <FullLoader />}
        </div>
    )
}

export default EditCarwash;
